import * as Icons from '@ant-design/icons';
import useNotification from 'hooks/useNotification';
import { Alert, Button, Typography } from 'antd';
import { IPersonalInfo } from 'interfaces/users';
import { useCertificateStore } from '../../certificate.store';
import { useCustomMutation, useNavigation } from '@refinedev/core';
import { useMemo } from 'react';

const PENDING_TYPES = {
  PERSONAL_INFO: 'personal_info',
  DOCUMENTS: 'documents'
} as const;

type PendingType = (typeof PENDING_TYPES)[keyof typeof PENDING_TYPES];

const isPersonalInfoComplete = (
  personalInfo: IPersonalInfo | null
): boolean => {
  return !!(personalInfo?.full_name && personalInfo?.phone_number);
};

const hasRequiredDocuments = (documentationStatus: string): boolean => {
  return documentationStatus === 'approved';
};

export const AlertPendingCertificate = () => {
  const { enrollment } = useCertificateStore();
  const { mutateAsync, isLoading } = useCustomMutation();
  const { push } = useNavigation();
  const { showSuccess, showError } = useNotification();

  if (!enrollment) {
    return null;
  }

  const pendingIssues = useMemo(() => {
    const issues = new Set<PendingType>();

    if (!isPersonalInfoComplete(enrollment.user.personal_info)) {
      issues.add(PENDING_TYPES.PERSONAL_INFO);
    }

    if (!hasRequiredDocuments(enrollment.documentation_status ?? '')) {
      issues.add(PENDING_TYPES.DOCUMENTS);
    }

    return issues;
  }, [enrollment]);

  const pendingLabels: Record<PendingType, string> = {
    [PENDING_TYPES.PERSONAL_INFO]: 'Dados pessoais',
    [PENDING_TYPES.DOCUMENTS]: 'Documentos'
  };

  if (pendingIssues.size === 0) {
    return null;
  }

  const canReopen =
    enrollment.certificate_status === 'rejected' &&
    enrollment.documentation_status === 'rejected';

  const handleReopen = async () => {
    try {
      await mutateAsync({
        url: `admin/graduate/courses/enrollments/${enrollment.id}/certification_approvals/reset_approvals`,
        method: 'put',
        values: { enrollment_id: enrollment.id }
      });

      showSuccess(
        'Pendência reaberta: certificado voltou para reavaliação da documentação.'
      );

      push(`${window.location.pathname}?tab=user_info&refresh=${Date.now()}`);
    } catch (error) {
      showError(
        'Não foi possível adicionar a documentação para reavaliação. Tente novamente.'
      );
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  return (
    <Alert
      type="warning"
      showIcon
      icon={<Icons.ExclamationCircleOutlined style={{ fontSize: 24 }} />}
      className="mb-4"
      action={
        canReopen && (
          <Button
            size="small"
            icon={<Icons.SyncOutlined />}
            onClick={handleReopen}
            loading={isLoading}
          >
            Reabrir pendência
          </Button>
        )
      }
      message={
        <div className="mb-4">
          <Typography.Title level={5} style={{ marginBottom: 0 }}>
            Pendências deste certificado
          </Typography.Title>
          <Typography.Text type="secondary" className="mb-4">
            Os seguintes dados estão incompletos ou precisam de aprovação:
          </Typography.Text>
        </div>
      }
      description={
        <ul className="list-inside pl-1 space-y-2 mb-0 text-neutral-500">
          {[...pendingIssues].map(issueType => (
            <li key={issueType}>{pendingLabels[issueType]}</li>
          ))}
        </ul>
      }
    />
  );
};
